import {
    AutocompleteInput,
    Create,
    DateInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ClipboardList, DollarSign, User, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router";
import { required, useCreate } from "ra-core";
import { FileInput } from "@/components/admin/file-input";
import { AutoReferenceField } from "../documents/shared/AutoReferenceField";
import { FilteredContactInput } from "../misc/FilteredContactInput";
import { AttachmentField } from "../notes/AttachmentField";
import { ProductRequirements } from "./ProductRequirements";

export const EnquiryCreate = () => {
    const [create] = useCreate();
    return (
        <Create title="Capture New Enquiry">
            <SimpleForm className="max-w-5xl mx-auto space-y-6">
                {/* Top Left Back Button */}
                <div className="flex justify-start w-full">
                    <Button asChild variant="outline" size="sm" className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                        <Link to="/enquiries" className="flex items-center gap-1.5">
                            <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back to Enquiries
                        </Link>
                    </Button>
                </div>

                {/* Header Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-slate-50/50">
                        <div className="flex items-center gap-2">
                            <ClipboardList className="w-5 h-5 text-slate-500" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Enquiry Details</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <AutoReferenceField docType="enquiry" />
                            <SelectInput
                                source="source"
                                label="Lead Source"
                                validate={required()}
                                choices={[
                                    { id: 'Website', name: 'Website' },
                                    { id: 'Email', name: 'Email' },
                                    { id: 'Phone', name: 'Phone / WhatsApp' },
                                    { id: 'Reference', name: 'Reference' },
                                    { id: 'Cold Call', name: 'Cold Calling' },
                                ]}
                                className="w-full"
                            />
                            <SelectInput
                                source="status"
                                label="Status"
                                defaultValue="new"
                                choices={[
                                    { id: 'new', name: 'New' },
                                    { id: 'in_discussion', name: 'In Discussion' },
                                    { id: 'closed', name: 'Closed' },
                                ]}
                                className="w-full"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Customer Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-slate-50/50">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5 text-slate-500" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Customer Information</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <ReferenceInput source="assigned_to" reference="sales">
                                <AutocompleteInput label="Assigned Sales Rep" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                            {/* V-8: Initial Score (qualification_score) removed per client request */}
                        </div>
                        <div className="pt-4">
                            <FileInput
                                source="attachments"
                                label="Attachments (drawings, PDF, images)"
                                multiple
                                accept={{
                                    "application/pdf": [".pdf"],
                                    "image/*": [".png", ".jpg", ".jpeg"],
                                    "application/msword": [".doc"],
                                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                                    "application/vnd.ms-excel": [".xls"],
                                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                                }}
                            >
                                <AttachmentField source="src" title="title" target="_blank" />
                            </FileInput>
                        </div>
                    </CardContent>
                </Card>

                {/* Requirements Card (E-3 / V-9: Budget removed per client request) */}
                <Card className="w-full shadow-sm border-t-4 border-t-slate-900">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-slate-500" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Requirements</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <DateInput source="requirements.timeline" label="Expected Timeline" className="w-full" />
                            <ProductRequirements />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <ReferenceInput source="company_id" reference="companies">
                                <AutocompleteInput
                                    label="Company"
                                    className="w-full"
                                    onCreate={async (value?: string) => {
                                        const name = value?.trim();
                                        if (!name) return undefined;
                                        const { data } = await create(
                                            "companies",
                                            { data: { name } },
                                            { returnPromise: true },
                                        );
                                        return data;
                                    }}
                                />
                            </ReferenceInput>
                            <FilteredContactInput label="Contact Person" className="w-full" allowCreate />
                            <ReferenceInput source="ops_head_id" reference="sales">
                                <AutocompleteInput label="Assigned Ops Head" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                            <ReferenceInput source="ops_team_member_id" reference="sales">
                                <AutocompleteInput label="Assigned Ops Team Member" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                        </div>
                    </CardContent>
                </Card>
            </SimpleForm>
        </Create>
    );
};
